import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
} from '@mui/material';
import {useCreateDestination} from '../../../hooks/productions/useProductions';
import {
  CreateDestinationRequestParams,
  CreateDestinationRequestBody,
} from '../../../api/types/production.type';
import '../Demo.scss';

const CreateDestinationApiTest: React.FC = () => {
  const [rootPath, setRootPath] = useState<string>(
    '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\Productions\\dndproject'
  );
  const [destinationRootPath, setDestinationRootPath] = useState<string>(
    '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\Productions\\dndproject'
  );
  const [destinationFolder, setDestinationFolder] = useState<string>('ccprod');
  const [createResult, setCreateResult] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const createMutation = useCreateDestination(
    (data) => {
      setCreateResult(
        `✅ ${data.success ? 'Success' : 'Failed'}: ${data.message}`
      );
    },
    (error) => {
      setCreateResult(`❌ Create failed: ${error.message}`);
    }
  );

  const handleCreate = () => {
    const params: CreateDestinationRequestParams = {
      rootPath: rootPath,
    };
    const body: CreateDestinationRequestBody = {
      RootPath: destinationRootPath,
      DestinationFolder: destinationFolder,
    };
    createMutation.mutate({params, body});
  };

  const handleReset = () => {
    setCreateResult('');
  };

  const handleCopyJson = async () => {
    if (createMutation.data) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(createMutation.data, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Create Destination API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Create a production destination folder. Creates the destination folder
          at the specified root path.
        </Typography>
      </Box>

      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="POST" color="success" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Create Destination
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/destination
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Create a production destination folder. POST request with query
            parameter (rootPath) and request body (RootPath, DestinationFolder).
            Returns success status and message.
          </Typography>
        </CardContent>
      </Card>

      {/* Request Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Query Parameters
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 3,
            }}
          >
            <TextField
              label="Root Path (Query Param)"
              value={rootPath}
              onChange={(e) => setRootPath(e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g., \\\\server\\share\\path"
              helperText="Query parameter - use double backslashes"
            />
          </Box>

          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
            sx={{marginTop: 2}}
          >
            Request Body
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              label="Root Path (Body)"
              value={destinationRootPath}
              onChange={(e) => setDestinationRootPath(e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g., \\\\server\\share\\path"
              helperText="Request body - use double backslashes"
            />
            <TextField
              label="Destination Folder"
              value={destinationFolder}
              onChange={(e) => setDestinationFolder(e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g., ccprod"
            />
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreate}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending
                ? '⏳ Creating...'
                : '🚀 Create Destination'}
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              🔄 Reset
            </Button>
            {createMutation.data && (
              <Button variant="outlined" onClick={handleCopyJson}>
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Loading State */}
      {createMutation.isPending && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">
              ⏳ Creating destination folder... Please wait...
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {createMutation.isError && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {createMutation.error?.message ||
                  'An error occurred while creating destination'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Success State */}
      {createMutation.isSuccess && createMutation.data && (
        <>
          <Card sx={{marginBottom: 3}}>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                Create Result
              </Typography>

              <Alert
                severity={createMutation.data.success ? 'success' : 'warning'}
                sx={{marginBottom: 2}}
              >
                <Typography className="typo-text subhead3_size" gutterBottom>
                  {createMutation.data.success ? '✅ Success' : '⚠️ Warning'}
                </Typography>
                <Typography className="typo-text body3_size">
                  {createMutation.data.message}
                </Typography>
              </Alert>

              <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                <Chip
                  label={`Success: ${createMutation.data.success ? 'Yes' : 'No'}`}
                  color={createMutation.data.success ? 'success' : 'default'}
                  size="small"
                />
                <Chip
                  label={`Already Exists: ${createMutation.data.alreadyExists ? 'Yes' : 'No'}`}
                  color={
                    createMutation.data.alreadyExists ? 'warning' : 'default'
                  }
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>

          {/* JSON Response */}
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                JSON Response
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(createMutation.data, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* Result Display */}
      {createResult && !createMutation.isPending && (
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              gutterBottom
            >
              Create Result
            </Typography>
            <Alert severity={createResult.includes('✅') ? 'success' : 'error'}>
              {createResult}
            </Alert>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CreateDestinationApiTest;
